<template>
  <div class="brc">
    <!-- No categories yet -->
    <div v-if="loaded && categories.length === 0" class="brc-empty">
      <feather-icon icon="GridIcon" size="30" class="brc-empty__icon"/>
      <p class="brc-empty__text">{{ msg('roomcategoriesintro') }}</p>
      <b-button variant="primary" size="sm" @click="goToRoomSettings">
        <feather-icon icon="SlidersIcon" class="mr-25"/>
        {{ msg('Room Settings') }}
      </b-button>
    </div>

    <template v-else>
      <!-- Total target -->
      <div class="brc-target">
        <label class="brc-target__label">{{ msg('howmanyroomsintotal') }}</label>
        <b-form-input v-model.number="totalTarget" type="number" min="1" :max="maxRooms" class="brc-target__input"/>
        <div class="brc-target__meter" :class="targetStateClass">
          <feather-icon :icon="runningTotal === (totalTarget || 0) && runningTotal > 0 ? 'CheckCircleIcon' : 'InfoIcon'" size="15"/>
          <span><b>{{ runningTotal }}</b><template v-if="totalTarget > 0"> / {{ totalTarget }}</template> {{ msg('hm_rooms') }}</span>
        </div>
      </div>

      <!-- Batch rows -->
      <div class="brc-batches">
        <div v-for="(batch, index) in batches" :key="index" class="brc-batch">
          <div class="brc-batch__grid">
            <b-form-group :label="msg('Count')" class="brc-batch__count">
              <b-form-input v-model.number="batch.count" type="number" min="1" :max="maxRooms"/>
            </b-form-group>
            <b-form-group :label="msg('roomcategory')" class="brc-batch__cat">
              <v-select
                  v-model="batch.roomCategoryId"
                  :options="categories"
                  :reduce="c => c.id"
                  label="name"
                  :dir="isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  append-to-body
              >
                <template #option="option">
                  <span class="brc-cat-dot" :style="{backgroundColor: option.color || '#7367f0'}"></span>
                  {{ option.name }}
                  <small v-if="option.basePricePerNight != null" class="text-muted"> · {{ option.basePricePerNight }}€</small>
                </template>
              </v-select>
            </b-form-group>
            <b-form-group :label="msg('nameprefix')" class="brc-batch__prefix">
              <b-form-input v-model="batch.namePrefix" :placeholder="msg('hm_room')"/>
            </b-form-group>
            <b-form-group :label="msg('startnumber')" class="brc-batch__start">
              <b-form-input v-model.number="batch.startNumber" type="number" min="0"/>
            </b-form-group>
            <b-form-group :label="msg('floor')" class="brc-batch__floor">
              <b-form-input v-model.number="batch.floorNumber" type="number"/>
            </b-form-group>
            <div class="brc-batch__remove">
              <b-button variant="flat-danger" class="btn-icon" :disabled="batches.length === 1"
                        @click="removeBatch(index)">
                <feather-icon icon="Trash2Icon"/>
              </b-button>
            </div>
          </div>

          <!-- Live name preview -->
          <div class="brc-preview" v-if="batch.count > 0">
            <span class="brc-preview__label">{{ msg('preview') }}:</span>
            <span v-for="name in previewNames(batch).names" :key="name"
                  class="brc-preview__chip" :class="{'brc-preview__chip--dup': duplicateNames.has(name)}">
              {{ name }}
            </span>
            <span v-if="previewNames(batch).more > 0" class="brc-preview__more">
              +{{ previewNames(batch).more }}
            </span>
          </div>
        </div>
      </div>

      <div class="brc-actions">
        <b-button variant="outline-primary" size="sm" @click="addBatch">
          <feather-icon icon="PlusIcon" class="mr-25"/>
          {{ msg('roomcategory') }}
        </b-button>
        <div class="brc-actions__right">
          <span v-if="hasDuplicates" class="brc-warn">
            <feather-icon icon="AlertTriangleIcon" size="14"/>
            {{ msg('someroomnamesduplicated') }}
          </span>
          <b-button variant="primary" :disabled="!canCreate || creating" @click="create">
            <b-spinner v-if="creating" small class="mr-25"/>
            <feather-icon v-else icon="CheckIcon" class="mr-25"/>
            {{ msg('addrooms') }} ({{ runningTotal }})
          </b-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'

export default {
  name: 'BulkRoomCreator',
  props: {
    buildingId: {type: String, required: true},
    buid: {type: Number, required: true},
  },
  data() {
    return {
      loaded: false,
      creating: false,
      totalTarget: null,
      maxRooms: 500,
      batches: [this.newBatch()],
    }
  },
  computed: {
    ...mapGetters('roomCategory', {categories: 'getRoomCategories'}),
    runningTotal() {
      return this.batches.reduce((sum, b) => sum + (b.count > 0 ? b.count : 0), 0);
    },
    targetStateClass() {
      if (this.totalTarget == null || this.totalTarget <= 0)
        return '';
      return this.runningTotal === this.totalTarget ? 'brc-target__meter--ok' : 'brc-target__meter--warn';
    },
    allNames() {
      let names = [];
      this.batches.forEach(batch => {
        if (!(batch.count > 0))
          return;
        const prefix = batch.namePrefix != null && batch.namePrefix.trim() !== '' ? batch.namePrefix.trim() + ' ' : '';
        const start = batch.startNumber != null ? batch.startNumber : 1;
        for (let i = 0; i < Math.min(batch.count, this.maxRooms); i++)
          names.push(prefix + (start + i));
      });
      return names;
    },
    duplicateNames() {
      const seen = new Set();
      const dups = new Set();
      this.allNames.forEach(name => {
        if (seen.has(name))
          dups.add(name);
        seen.add(name);
      });
      return dups;
    },
    hasDuplicates() {
      return this.duplicateNames.size > 0;
    },
    canCreate() {
      return this.runningTotal >= 1
          && this.runningTotal <= this.maxRooms
          && !this.hasDuplicates
          && this.batches.every(b => b.roomCategoryId != null && b.count >= 1);
    },
  },
  created() {
    let $this = this;
    this.$store.dispatch('roomCategory/getAllForOrg').finally(() => $this.loaded = true);
  },
  methods: {
    ...mapActions('realEstate', ['bulkCreateRooms']),
    newBatch() {
      return {roomCategoryId: null, count: 1, namePrefix: '', startNumber: 101, floorNumber: null};
    },
    addBatch() {
      // continue numbering where the previous batch ends
      const last = this.batches[this.batches.length - 1];
      const batch = this.newBatch();
      if (last != null && last.startNumber != null && last.count > 0)
        batch.startNumber = last.startNumber + last.count;
      this.batches.push(batch);
    },
    removeBatch(index) {
      if (this.batches.length > 1)
        this.batches.splice(index, 1);
    },
    previewNames(batch) {
      const cap = 12;
      const prefix = batch.namePrefix != null && batch.namePrefix.trim() !== '' ? batch.namePrefix.trim() + ' ' : '';
      const start = batch.startNumber != null ? batch.startNumber : 1;
      const count = Math.min(batch.count || 0, this.maxRooms);
      const names = [];
      for (let i = 0; i < Math.min(count, cap); i++)
        names.push(prefix + (start + i));
      return {names, more: Math.max(count - cap, 0)};
    },
    goToRoomSettings() {
      this.$router.push('/room-settings');
    },
    create() {
      if (!this.canCreate || this.creating)
        return;
      let $this = this;
      this.creating = true;
      const request = {
        buildingId: this.buildingId,
        buid: this.buid,
        batches: this.batches.map(b => ({
          roomCategoryId: b.roomCategoryId,
          count: b.count,
          namePrefix: b.namePrefix != null && b.namePrefix.trim() !== '' ? b.namePrefix.trim() : null,
          startNumber: b.startNumber,
          floorNumber: b.floorNumber,
        })),
      };
      this.bulkCreateRooms(request)
          .then(ans => {
            $this.$emit('created', ans['RealEstateIds']);
            $this.batches = [$this.newBatch()];
            $this.totalTarget = null;
          })
          .finally(() => $this.creating = false);
    },
  }
}
</script>

<style lang="scss" scoped>
.brc-empty {
  text-align: center;
  padding: 1.5rem 1rem;

  &__icon {
    color: #b9b9c3;
    margin-bottom: 0.5rem;
  }

  &__text {
    color: #6e6b7b;
    max-width: 420px;
    margin: 0 auto 0.75rem;
  }
}

.brc-target {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border: 1px dashed rgba(115, 103, 240, 0.4);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  background: rgba(115, 103, 240, 0.04);

  &__label {
    font-weight: 600;
    margin: 0;
  }

  &__input {
    width: 90px;
  }

  &__meter {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-left: auto;
    padding: 0.25rem 0.6rem;
    border-radius: 1rem;
    background: rgba(34, 41, 47, 0.06);
    font-size: 0.9rem;

    &--ok {
      background: rgba(40, 199, 111, 0.15);
      color: #28c76f;
    }

    &--warn {
      background: rgba(255, 159, 67, 0.15);
      color: #ff9f43;
    }
  }
}

.brc-batch {
  border: 1px solid rgba(34, 41, 47, 0.1);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem 0.5rem;
  margin-bottom: 0.75rem;

  &__grid {
    display: grid;
    grid-template-columns: 90px minmax(160px, 1.4fr) minmax(120px, 1fr) 110px 90px 42px;
    gap: 0.75rem;
    align-items: end;

    @media (max-width: 992px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__remove {
    padding-bottom: 1rem;
  }
}

.brc-cat-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}

.brc-preview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding: 0.25rem 0 0.5rem;

  &__label {
    font-size: 0.8rem;
    color: #6e6b7b;
    margin-right: 0.2rem;
  }

  &__chip {
    font-size: 0.75rem;
    padding: 0.15rem 0.5rem;
    border-radius: 1rem;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;

    &--dup {
      background: rgba(234, 84, 85, 0.12);
      color: #ea5455;
      text-decoration: line-through;
    }
  }

  &__more {
    font-size: 0.75rem;
    color: #6e6b7b;
  }
}

.brc-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  &__right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
}

.brc-warn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #ff9f43;
  font-size: 0.85rem;
}

.dark-layout {
  .brc-batch {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .brc-target {
    background: rgba(115, 103, 240, 0.08);

    &__meter {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .brc-empty__text, .brc-preview__label, .brc-preview__more {
    color: #b4b7bd;
  }
}
</style>
